import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import type { OrderConfirmationResponse } from "../orders/ordersApi";
import { acceptOrderConfirmation, getOrderConfirmation } from "../orders/ordersApi";

export const useOrderConfirmation = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { orderId, confirmationId } = useParams<{ orderId: string; confirmationId: string }>();
  if (!orderId || !confirmationId) {
    throw new Error("useOrderConfirmation requires orderId and confirmationId route params");
  }
  const { data, isLoading, error } = useQuery({
    queryKey: ["orders", orderId, "confirmations", confirmationId],
    queryFn: ({ signal }) => getOrderConfirmation(orderId, confirmationId, signal),
  });

  useEffect(() => {
    if (!isLoading && error) {
      enqueueSnackbar(`Nie udało się pobrać potwierdzenia. Powód: ${error.message}`, {
        variant: "error",
      });
    }
  }, [isLoading, error]);

  const acceptConfirmationMutation = useMutation<OrderConfirmationResponse, unknown, string>({
    mutationFn: (nameAndSurname) => acceptOrderConfirmation(orderId, confirmationId, nameAndSurname),
    onSuccess: (data) => {
      enqueueSnackbar(`Potwierdzenie zamówienia zostało zaakceptowane`, { variant: "success" });
      queryClient.setQueryData(["orders", orderId, "confirmations", confirmationId], data);
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaakceptować potwierdzenia. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  return {
    confirmation: data,
    isLoading,
    acceptConfirmation: (nameAndSurname: string) => acceptConfirmationMutation.mutateAsync(nameAndSurname),
  };
};
